// The Calls page's reads: whether phone lines are switched on for this site, the event's line, and who is calling. Each is
// read under the person's own session, so the data tier decides who sees it.
import { supabase } from '@/lib/supabase';
import { noteApiFailure, noteFailure } from '@/lib/failures';
import { postApi } from '@/lib/api';
import { fetchAll } from '@/lib/fetchAll';
import { writeUpdate } from '@/lib/write';
import type { AssistAnswer, CallQuestionRow, QuestionStatus, SentQuestion } from './questions';

export interface EventLine {
  id: string; number: string; ring_seconds: number; closing_message: string | null; active: boolean;
  host_number: string | null; host_number_verified_at: string | null;
  /** Callers nobody answers may leave a message. false while the data tier has no such setting yet. */
  voicemail_enabled: boolean;
  /** Every answered call is recorded and summarized, after the other person hears that it may be. On by default. */
  record_calls: boolean;
  /** When calls ring the team. Each is undefined while the data tier has no such setting yet. Times come back 'HH:MM:SS';
   * both null = no set hours. */
  hours_open?: string | null; hours_close?: string | null; hours_tz?: string;
  closed_shabbos_yt?: boolean; closed_now?: boolean;
}

/** Why the line isn't ringing the team right now: closed by hand, Shabbos or Yom Tov, or outside its hours. */
export type ClosedReason = 'manual' | 'shabbos_yt' | 'hours';
/** The zones a line's hours can be set in, with plain names. The server keeps the same list. */
export const ZONES: { tz: string; label: string }[] = [
  { tz: 'America/New_York', label: 'Eastern (New York)' }, { tz: 'America/Chicago', label: 'Central (Chicago)' },
  { tz: 'America/Denver', label: 'Mountain (Denver)' }, { tz: 'America/Phoenix', label: 'Arizona' },
  { tz: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' }, { tz: 'America/Anchorage', label: 'Alaska' },
  { tz: 'Pacific/Honolulu', label: 'Hawaii' }, { tz: 'America/Toronto', label: 'Toronto' },
  { tz: 'Europe/London', label: 'London' }, { tz: 'Asia/Jerusalem', label: 'Jerusalem' },
];
export const DEFAULT_ZONE = 'America/New_York';
/** '10:00:00' → '10:00'; nothing → ''. */
export const toHHMM = (t: string | null | undefined) => (t ? t.slice(0, 5) : '');
/** true = the saved line has a closure set: hours, Shabbos and Yom Tov, or closed by hand. */
export const hasClosure = (l: EventLine) => Boolean(l.hours_open) || l.closed_shabbos_yt === true || l.closed_now === true;

const STATUS = '/api/voice/status';
export const STATUS_FAILED = 'Couldn’t check whether phone lines are available — try again.';

/** true = this site can give events a phone line. */
export async function linesAvailable(): Promise<boolean> {
  const { data } = await supabase.auth.getSession();
  const r = await fetch(STATUS, { headers: { Authorization: `Bearer ${data.session?.access_token ?? ''}` }, cache: 'no-store' }).catch(() => null);
  const j = r?.ok ? ((await r.json().catch(() => null)) as { configured?: unknown } | null) : null;
  if (!j || typeof j.configured !== 'boolean') {
    noteFailure({ reason: r ? 'server' : 'network', message: STATUS_FAILED, apiPath: STATUS, apiStatus: r?.status });
    throw new Error(STATUS_FAILED);
  }
  return j.configured;
}

const LINE_STATUS = '/api/voice/line-status';
export const LINE_STATUS_FAILED = 'Couldn’t check whether the line is open right now.';

/** Whether the event's line rings the team right now. reason null = open. `next_open` ('HH:MM') when the server says it. */
export async function lineStatus(eventId: string): Promise<{ reason: ClosedReason | null; next_open: string | null }> {
  const { data } = await supabase.auth.getSession();
  const r = await fetch(`${LINE_STATUS}?event=${encodeURIComponent(eventId)}`, { headers: { Authorization: `Bearer ${data.session?.access_token ?? ''}` }, cache: 'no-store' }).catch(() => null);
  const j = r?.ok ? ((await r.json().catch(() => null)) as { reason?: unknown; next_open?: unknown } | null) : null;
  if (!j || !('reason' in j) || (j.reason !== null && typeof j.reason !== 'string')) {
    noteFailure({ reason: r ? 'server' : 'network', message: LINE_STATUS_FAILED, apiPath: LINE_STATUS, apiStatus: r?.status });
    throw new Error(LINE_STATUS_FAILED);
  }
  return { reason: (j.reason as ClosedReason | null), next_open: typeof j.next_open === 'string' ? j.next_open : null };
}

const LINE_COLS = 'id, number, ring_seconds, closing_message, active, host_number, host_number_verified_at, record_calls';
const HOURS_COLS = 'hours_open, hours_close, hours_tz, closed_shabbos_yt, closed_now';

export async function eventLine(eventId: string): Promise<EventLine | null> {
  const h = await supabase.from('event_lines').select(`${LINE_COLS}, voicemail_enabled, ${HOURS_COLS}`).eq('event_id', eventId).eq('active', true).maybeSingle();
  if (h.error === null) return (h.data as EventLine | null) ?? null;
  if (h.error.code !== '42703') throw h.error;
  // the data tier doesn't have the hours yet: read the line without them (hours left unset)
  const r = await supabase.from('event_lines').select(`${LINE_COLS}, voicemail_enabled`).eq('event_id', eventId).eq('active', true).maybeSingle();
  if (r.error?.code === '42703') {
    // the data tier doesn't have the message setting yet: read the line without it, messages off
    const o = await supabase.from('event_lines').select(LINE_COLS).eq('event_id', eventId).eq('active', true).maybeSingle();
    if (o.error) throw o.error;
    return o.data ? { ...(o.data as Omit<EventLine, 'voicemail_enabled'>), voicemail_enabled: false } : null;
  }
  if (r.error) throw r.error;
  return (r.data as EventLine | null) ?? null;
}

/** The signed-in person's own cell, as far as they may read it: the number and whether it rings yet. */
export async function myCell(): Promise<{ cell: string; verified_at: string | null } | null> {
  const r = await supabase.from('user_cells').select('cell, verified_at').maybeSingle();
  if (r.error) throw r.error;
  return (r.data as { cell: string; verified_at: string | null } | null) ?? null;
}

/** Who rang an event's line, for the banner: the caller's number and the event's name, by our own id for the call.
 * null = not one of ours, or not readable by this person (the banner then says so plainly). */
export async function callFrom(id: string): Promise<{ number: string | null; event: string | null } | null> {
  const r = await supabase.from('event_calls').select('from_number, events(event_name)').eq('id', id).maybeSingle();
  if (r.error || !r.data) return null;
  const d = r.data as { from_number: string | null; events: { event_name: string | null } | { event_name: string | null }[] | null };
  const ev = Array.isArray(d.events) ? d.events[0] : d.events;
  return { number: d.from_number, event: ev?.event_name ?? null };
}

/** "(845) 555-0100", "845.555.0100", "1 845 555 0100" → "+18455550100"; anything else → null. Same rule as the server. */
export function toE164(input: string): string | null {
  if (input.length > 32 || /[^\d\s()+.-]/.test(input)) return null;
  const digits = input.replace(/\D/g, '');
  const ten = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  return ten.length === 10 && /^[2-9]\d{2}[2-9]\d{6}$/.test(ten) ? `+1${ten}` : null;
}

/** Where a call out stands, for the bar while it is on: read under the person's own session. null = not readable. */
export async function callOutcome(id: string): Promise<string | null> {
  const r = await supabase.from('event_calls').select('outcome').eq('id', id).maybeSingle();
  return r.error || !r.data ? null : (r.data as { outcome: string }).outcome;
}

/** A recording on a call, as far as the person may read it (never where the audio is kept): a message a caller left on a
 * call nobody answered, or an answered call itself (kind 'call'). A row without a kind is a message. */
export interface VoicemailRow {
  id: string; call_id: string; kind?: 'voicemail' | 'call';
  audio_status: 'recording' | 'saving' | 'stored' | 'failed' | 'empty'; duration_secs: number | null;
  transcript_status: 'pending' | 'ready' | 'none'; transcript: string | null;
  recap_status: 'pending' | 'running' | 'ready' | 'failed' | 'none'; recap: string | null; recap_callback: string | null; recap_urgent: boolean;
  heard_at: string | null; created_at: string;
}
// named columns only: the rest of the row is the server's, and asking for it is refused
const VM_COLS = 'id, call_id, kind, audio_status, duration_secs, transcript_status, transcript, recap_status, recap, recap_callback, recap_urgent, heard_at, created_at';
export const VM_LOAD_FAILED = 'Couldn’t load the messages — try again.';

/** One call's notes, kept by the team: who it was, how to reach them, the booking, why they called, whether to call back. */
export interface CallNoteRow {
  id: string; call_id: string; event_id: string;
  name: string | null; phone: string | null; email: string | null; booking_ref: string | null;
  reason: CallReason | null; needs_callback: boolean; notes: string | null;
  callback_cleared_at: string | null; updated_by: string; created_at: string; updated_at: string; version: number;
}
export type CallReason = 'booking' | 'change' | 'cancel' | 'question' | 'other';
// the columns the person may read; who made the note and its organization stay with the server
const NOTE_COLS = 'id, call_id, event_id, name, phone, email, booking_ref, reason, needs_callback, notes, callback_cleared_at, updated_by, created_at, updated_at, version';
export const NOTES_LOAD_FAILED = 'Couldn’t load the call notes.';

export interface CallRow {
  id: string; direction: 'in' | 'out_browser' | 'out_cell'; from_number: string | null; to_number: string | null; outcome: string;
  started_at: string; answered_at?: string | null; ended_at: string | null; answered_by: string | null; placed_by: string | null;
  voicemail?: VoicemailRow | null;
  note?: CallNoteRow | null;
  /** Who on the team took the call (answered it, or placed it), for the call panel's history. */
  who?: string | null;
  /** Set when the call came in while the line was closed, and why. */
  closed_reason?: ClosedReason | null;
}

/** How long the two sides talked, in seconds: answered to ended. null for a call nobody answered or one still going. */
export function callSeconds(c: Pick<CallRow, 'answered_at' | 'ended_at'>): number | null {
  if (!c.answered_at || !c.ended_at) return null;
  const s = Math.round((Date.parse(c.ended_at) - Date.parse(c.answered_at)) / 1000);
  return Number.isFinite(s) && s >= 0 ? s : null;
}
/** A call's length as minutes and seconds (2:05). */
export const callLength = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

/** What the call was about, in a line: the written summary of its recording or message, else the team's notes, else (a call
 * too short to summarize) the few words it heard, in quotes. */
export function callSummary(c: Pick<CallRow, 'voicemail' | 'note'>): string | null {
  const v = c.voicemail?.recap?.trim() || c.voicemail?.recap_callback?.trim() || c.note?.notes?.trim() || '';
  if (v) return v.replace(/\s+/g, ' ');
  const heard = c.voicemail?.transcript_status === 'ready' ? c.voicemail.transcript?.trim().replace(/\s+/g, ' ') : '';
  return heard ? `“${heard}”` : null;
}

/** The number at the other end of a call: who rang in, or who was called. */
export const otherParty = (c: Pick<CallRow, 'direction' | 'from_number' | 'to_number'>) => (c.direction === 'in' ? c.from_number : c.to_number);

/** How many of the event's latest calls Recent calls reads in full, to group them by number. */
export const CALLS_READ = 200;
/** How many of the event's latest calls are counted for each number's count (only who was on the other end is read). */
export const CALLS_COUNTED = 2000;

/** One number on Recent calls: its calls newest first (the first is the one the row shows), how many calls the number has
 * (counted further back than the calls read in full), and the name the team wrote on any of its notes. */
export interface NumberGroup { key: string; calls: CallRow[]; total: number; name: string | null }

/** The number a call is grouped under: the other end in one spelling, or the call itself when the number is withheld. */
const numberKey = (c: Pick<CallRow, 'id' | 'direction' | 'from_number' | 'to_number'>) => {
  const other = otherParty(c);
  return (other && (toE164(other) ?? other.trim())) || `call:${c.id}`;
};

/** The calls grouped by the number at the other end, like a phone's call log: one group a number, in any spelling, ordered by
 * each number's latest call. A call with no number (withheld) stays a group of its own. `counted` holds the counted calls
 * (CALLS_COUNTED); a number's total is never below the calls it shows. */
export function groupCalls(calls: CallRow[], counted: Pick<CallRow, 'id' | 'direction' | 'from_number' | 'to_number'>[] = []): NumberGroup[] {
  const groups = new Map<string, NumberGroup>();
  for (const c of [...calls].sort((a, b) => b.started_at.localeCompare(a.started_at))) {
    const key = numberKey(c);
    const g = groups.get(key);
    if (g) g.calls.push(c);
    else groups.set(key, { key, calls: [c], total: 0, name: null });
  }
  const totals = new Map<string, number>();
  for (const c of counted) { const k = numberKey(c); totals.set(k, (totals.get(k) ?? 0) + 1); }
  for (const g of groups.values()) {
    g.total = Math.max(g.calls.length, totals.get(g.key) ?? 0);
    g.name = g.calls.find((c) => c.note?.name?.trim())?.note?.name?.trim() ?? null;
  }
  return [...groups.values()];
}

const CALL_COLS = 'id, direction, from_number, to_number, outcome, started_at, answered_at, ended_at, answered_by, placed_by';

/** The event's calls, newest first, with the names of the people who answered or placed them, any recording and any notes.
 * Recordings or notes that can't be read leave the log as it is (noted for Report). */
export async function eventCalls(eventId: string): Promise<{ calls: CallRow[]; counted: Pick<CallRow, 'id' | 'direction' | 'from_number' | 'to_number'>[]; names: Record<string, string>; vmFailed: boolean; notesFailed: boolean }> {
  const read = (cols: string, n: number) => supabase.from('event_calls').select(cols).eq('event_id', eventId).order('started_at', { ascending: false }).limit(n);
  let r = await read(`${CALL_COLS}, closed_reason`, CALLS_READ);
  // the data tier doesn't know why a call found the line closed yet: read the calls without it
  if (r.error?.code === '42703') r = await read(CALL_COLS, CALLS_READ);
  if (r.error) throw r.error;
  const calls = (r.data ?? []) as unknown as CallRow[];
  // each number's count reaches further back; while it can't be read, the counts are the calls read in full
  let counted: Pick<CallRow, 'id' | 'direction' | 'from_number' | 'to_number'>[] = [];
  if (calls.length === CALLS_READ) {
    // a thousand rows a read: the data tier hands back no more than that at once
    const page = (from: number) => supabase.from('event_calls').select('id, direction, from_number, to_number').eq('event_id', eventId)
      .order('started_at', { ascending: false }).order('id').range(from, from + 999);
    const parts = await Promise.all(Array.from({ length: CALLS_COUNTED / 1000 }, (_, i) => page(i * 1000)));
    if (parts.every((k) => !k.error)) counted = parts.flatMap((k) => (k.data ?? []) as unknown as typeof counted);
  }
  let vmFailed = false;
  let notesFailed = false;
  if (calls.length) {
    const ids = calls.map((c) => c.id);
    // fifty ids a request, so the address of each read stays short
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += 50) chunks.push(ids.slice(i, i + 50));
    const each = async <T,>(ask: (part: string[]) => PromiseLike<{ data: unknown; error: unknown }>) => {
      const rs = await Promise.all(chunks.map(ask));
      const failed = rs.find((x) => x.error);
      return failed ? { data: null, error: failed.error } : { data: rs.flatMap((x) => (x.data ?? []) as T[]), error: null };
    };
    const [v, n] = await Promise.all([
      each<VoicemailRow>((part) => supabase.from('event_voicemails').select(VM_COLS).in('call_id', part)),
      each<CallNoteRow>((part) => supabase.from('event_call_notes').select(NOTE_COLS).in('call_id', part)),
    ]);
    if (v.error) { vmFailed = true; noteFailure({ reason: 'server', message: VM_LOAD_FAILED }); }
    else {
      const byCall = new Map(((v.data ?? []) as VoicemailRow[]).map((m) => [m.call_id, m]));
      for (const c of calls) c.voicemail = byCall.get(c.id) ?? null;
    }
    if (n.error) { notesFailed = true; noteFailure({ reason: 'server', message: NOTES_LOAD_FAILED }); }
    else {
      const byCall = new Map(((n.data ?? []) as CallNoteRow[]).map((m) => [m.call_id, m]));
      for (const c of calls) c.note = byCall.get(c.id) ?? null;
    }
  }
  const ids = [...new Set(calls.flatMap((c) => [c.answered_by, c.placed_by]).filter((v): v is string => Boolean(v)))];
  const names: Record<string, string> = {};
  if (ids.length) {
    const p = await supabase.from('user_profiles').select('id, display_name').in('id', ids);
    for (const u of (p.data ?? []) as { id: string; display_name: string | null }[]) if (u.display_name) names[u.id] = u.display_name;
  }
  return { calls, counted, names, vmFailed, notesFailed };
}

export interface VoicemailPlay { url: string; seconds: number | null }
export interface VoicemailRecap { recap_status: string; recap?: string | null; recap_callback?: string | null; recap_urgent?: boolean }

/** A short-lived link to play a message; asking for it marks it heard for the team. */
export function playVoicemail(id: string) {
  return postApi<VoicemailPlay>('/api/voice/voicemail/play', { voicemail_id: id });
}

/** Writes the message's recap now, when it is missing or didn't work the first time. */
export function recapVoicemail(id: string) {
  return postApi<VoicemailRecap>('/api/voice/voicemail/recap', { voicemail_id: id });
}

export const NOTE_LOAD_FAILED = 'Couldn’t load this call’s notes.';

export type NoteCall = Pick<CallRow, 'id' | 'direction' | 'from_number' | 'to_number' | 'ended_at'> & { event_id: string };

/** One call and its notes, for the notes popup. null call = not one this person may read. */
export async function callNote(callId: string): Promise<{ call: NoteCall | null; note: CallNoteRow | null }> {
  const [c, n] = await Promise.all([
    supabase.from('event_calls').select('id, event_id, direction, from_number, to_number, ended_at').eq('id', callId).maybeSingle(),
    supabase.from('event_call_notes').select(NOTE_COLS).eq('call_id', callId).maybeSingle(),
  ]);
  if (c.error) throw c.error;
  if (n.error) throw n.error;
  return { call: (c.data as NoteCall | null) ?? null, note: (n.data as CallNoteRow | null) ?? null };
}

/** Saves the keys given on the call's notes (the server keeps who and when). `version` goes with a change to the notes text,
 * so two people writing at once don't overwrite each other. */
export function saveCallNote(body: { call_id: string; version?: number } & Partial<Omit<CallNoteRow, 'id' | 'call_id' | 'event_id' | 'callback_cleared_at' | 'updated_by' | 'created_at' | 'updated_at' | 'version'>>) {
  return postApi<{ note?: CallNoteRow | null }>('/api/voice/call-notes', body);
}
export const NOTE_SAVE_FAILED = 'Couldn’t save the note — try again.';
export const NOTE_CHANGED = 'This note changed since you opened it.';

// copy of server/voice/callNotes.ts EMAIL: the popup asks about an address only once the server would save it
export const NOTE_EMAIL = /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/;

/** What the server says it would do for an email on this call: resend the booking's confirmation, invite someone with no
 * booking yet, or neither (waiting list, asked for no emails, booking closed). The server decides; the popup only shows it. */
export type MailAction = 'resend' | 'invite' | 'waitlist' | 'unsubscribed' | 'closed';
export interface MailCheck { action: MailAction; matches?: number; access_code?: boolean; reason?: 'closed' | 'not-yet' | 'ended' }
export interface MailSent { action: MailAction; status?: 'sent' | 'queued' | 'failed'; to?: string; matches?: number }
const CALL_INVITE = '/api/voice/call-invite';

/** Looks the typed email up on the call's event. Sends nothing. */
export function checkCallInvite(callId: string, email: string) {
  return postApi<MailCheck>(CALL_INVITE, { call_id: callId, check: true, email: email.trim() });
}
/** Emails the caller now. `email` is the one just saved on the note: the server mails the saved one and refuses if they differ. */
export function sendCallInvite(callId: string, email: string) {
  return postApi<MailSent>(CALL_INVITE, { call_id: callId, email: email.trim() });
}
export const MAIL_CHECKING = 'Checking…';
export const MAIL_SAVE_FIRST = 'Check the email and try again.';
export const MAIL_CHECK_FAILED = 'Couldn’t check this email — try again.';
export const MAIL_SEND_FAILED = 'The email could not be sent. Try again in a minute.';
/** The line under the Email field for the server's answer. The confirmation number is never shown: the guest's mailbox gets it. */
export function mailLine(c: MailCheck): string {
  const more = (c.matches ?? 0) > 1 ? ` ${c.matches} bookings use this address.` : '';
  const code = c.access_code ? ' The booking page asks for an access code.' : '';
  switch (c.action) {
    case 'resend': return `Has a booking on this event.${more}${code}`;
    case 'invite': return `No booking yet.${code}`;
    case 'waitlist': return 'On the waiting list.';
    case 'unsubscribed': return 'This guest asked not to get emails.';
    default:
      return c.reason === 'not-yet' ? 'Online booking has not opened yet for this event.'
        : c.reason === 'ended' ? 'This event has ended.' : 'Online booking is closed for this event.';
  }
}
/** The one button for an answer, or none: only a booking or no booking at all can be emailed from here. */
export const mailButton = (a: MailAction | undefined): string | null =>
  a === 'resend' ? 'Resend confirmation' : a === 'invite' ? 'Send invite email' : null;
/** The notice after a send. null = nothing went out (the server answered with a reason instead). */
export function sentNotice(s: MailSent): string | null {
  if (!s.to || (s.status !== 'sent' && s.status !== 'queued') || (s.action !== 'resend' && s.action !== 'invite')) return null;
  const resend = s.action === 'resend';
  return s.status === 'sent' ? `${resend ? 'Confirmation resent' : 'Invite sent'} to ${s.to}.`
    : `${resend ? 'The confirmation' : 'The invite'} to ${s.to} is queued — it will go out within a minute.`;
}

export const DIAL_API = '/api/voice/dial';
export const DIAL_FAILED = 'Couldn’t place the call just now — try again in a minute.';

// copy of server/voice/numbers.ts; tests/unit/call-back-number.test.ts keeps them equal
export const NEVER_A_LINE = new Set(['+18452618070', '+18454609049']);
export const NOT_A_PHONE = new Set(['242', '246', '264', '268', '284', '345', '441', '473', '649', '658', '664', '670', '671', '684',
  '721', '758', '767', '784', '809', '829', '849', '868', '869', '876', '900', '800', '833', '844', '855', '866', '877', '888',
  '456', '500', '521', '522', '523', '524', '525', '526', '527', '528', '529', '532', '533', '535', '538', '542', '543', '544',
  '545', '546', '547', '549', '550', '552', '553', '554', '556', '558', '566', '569', '577', '578', '588', '589', '600', '622',
  '700', '710', '822', '880', '881', '882', '883', '884', '885', '886', '887', '889']);

/** true = the number can be called back: a US or Canada number that is someone's own phone (not withheld, toll-free,
 * premium, an island billed as international, or one of our own lines). The server checks again. */
export function canCallBack(n: string | null | undefined): boolean {
  const e = toE164(n ?? '');
  return e !== null && !NEVER_A_LINE.has(e) && !NOT_A_PHONE.has(e.slice(2, 5)) && e.slice(5, 8) !== '976';
}

export const STAFF_BOOK_API = '/api/staff/registrations';
/** A booking made by staff on a call: the guest object the public form sends, how it is paid, and the two figures the screen showed. */
export interface StaffBookBody {
  event_id: string; call_id: string | null; idempotency_key: string; guest: Record<string, unknown>;
  companions: never[]; quantities: never[]; pay: StaffPay; hold?: StaffHold | null; card: unknown;
  client_total_cents: number; expect_charge_cents: number; internal_note: string | null; heard: string | null;
}
/** How the guest pays: card on the call, the emailed pay link, or a hold (nothing charged; the room is released when it ends unpaid). */
export type StaffPay = 'card' | 'link' | 'hold';
/** A hold's length: whole hours (1 to 8760) or never; neither = the event's own default. */
export interface StaffHold { hours?: number; never?: boolean }
export const HOLD_MAX_HOURS = 8760;
export const HOLD_DEFAULT_HOURS = 24;
/** The event's default hold length in hours; 24 when it cannot be read (D10), and then `known` is false. */
export async function fetchHoldDefault(eventId: string): Promise<{ hours: number; known: boolean }> {
  const r = await supabase.from('events').select('hold_default_hours').eq('id', eventId).maybeSingle();
  const n = (r.data as { hold_default_hours?: unknown } | null)?.hold_default_hours;
  return !r.error && typeof n === 'number' && Number.isInteger(n) && n >= 1 && n <= HOLD_MAX_HOURS ? { hours: n, known: true } : { hours: HOLD_DEFAULT_HOURS, known: false };
}
export type StaffMail = 'sent' | 'queued' | 'no-provider' | 'failed';
export interface StaffBooked {
  ok: true; confirmation_number: string | null; guest_id: string | null; total_cents: number; charged_cents: number; balance_cents: number;
  balance_due_date: string | null; payment: 'charged' | 'none'; mail: StaffMail; waitlisted_room_type?: string | null;
  /** A hold: when it ends, or that it never does (both absent on any other booking). */
  hold_expires_at?: string | null; hold_never?: boolean;
}
/** A booking not made. `payment` is set when a card was charged and what became of it is in doubt ('unsure', 'not-reversed') or settled ('reversed'). */
export interface StaffRefused { ok: false; error: string; message: string; payment?: 'unsure' | 'not-reversed' | 'reversed'; reason?: string; charge_cents?: number }
export type StaffBookResult = StaffBooked | StaffRefused;
export const STAFF_BOOK_FAILED = 'The booking could not be sent. Try again in a minute.';
/** A card was sent and no answer came back: it may have been charged. */
export const STAFF_BOOK_LOST = 'No answer came back, so the card may have been charged. Do not try again: check the guest list and the payments page first.';

/** Sends the booking. Every answer that is not a booking is noted for Report under the sentence the server sent. */
export async function staffBook(body: StaffBookBody): Promise<StaffBookResult> {
  const { data } = await supabase.auth.getSession();
  let r: Response;
  try {
    r = await fetch(STAFF_BOOK_API, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${data.session?.access_token ?? ''}` }, body: JSON.stringify(body) });
  } catch {
    // with a card on its way a dropped connection leaves the charge in doubt
    const message = body.card ? STAFF_BOOK_LOST : 'Connection failed — check your internet and try again.';
    noteFailure({ reason: 'network', message, apiPath: STAFF_BOOK_API });
    return { ok: false, error: 'network', message, ...(body.card ? { payment: 'unsure' as const } : {}) };
  }
  const j = (await r.json().catch(() => null)) as (Partial<StaffBooked> & Partial<Omit<StaffRefused, 'ok'>> & { ok?: boolean }) | null;
  if (r.ok && j?.ok === true) return j as StaffBooked;
  const message = (typeof j?.message === 'string' && j.message.trim()) || STAFF_BOOK_FAILED;
  const out: StaffRefused = { ok: false, error: typeof j?.error === 'string' ? j.error : 'failed', message };
  if (j?.payment === 'unsure' || j?.payment === 'not-reversed' || j?.payment === 'reversed') out.payment = j.payment;
  if (typeof j?.reason === 'string') out.reason = j.reason;
  if (typeof j?.charge_cents === 'number') out.charge_cents = j.charge_cents;
  if (r.ok) noteFailure({ reason: 'refused', message, apiPath: STAFF_BOOK_API, apiStatus: r.status, code: out.error });
  else noteApiFailure(STAFF_BOOK_API, r.status, message);
  return out;
}

// ── the call panel (call-center B2) ─────────────────────────────────────────────────────────────────────────────────────────

export const HOLD_API = '/api/voice/hold';
export const HOLD_FAILED = 'Couldn’t change the hold just now — try again.';
/** Puts the other person on hold (they hear the hold music) or takes them off it. `seq` numbers this call's presses. */
export function holdCall(callId: string, hold: boolean, seq: number) {
  return postApi<{ held: boolean }>(HOLD_API, { call_id: callId, hold, seq });
}

/** One person on the event the caller may be, as the server found them (by the call's number, or a note's email). */
export interface CallerPerson {
  id: string; name: string; email: string | null; phone: string | null; matched_by: 'phone' | 'email';
  confirmation_number: string | null; status: 'booked' | 'cancelled' | 'waitlist'; lead: boolean;
  room_type: string | null; check_in: string | null; check_out: string | null; hotel: string | null; hotel_confirmation: string | null;
  special_requests: string | null; party: string[]; booked_at: string;
  invoice: { total_cents: number; paid_cents: number; balance_cents: number; status: string; due_date: string | null } | null;
}
/** A prospect on the event's list with this number (G2): only the name, the flag, the tags and the newest note. */
export interface CallerProspect { id: string; name: string; flag: string | null; tags: string[]; note: string | null }
export interface CallerProfile { number: string | null; people: CallerPerson[]; prospects: CallerProspect[] }
/** The prospects in a caller answer, defensively: an older server sends none, a malformed entry is dropped. */
function callerProspects(v: unknown): CallerProspect[] {
  if (!Array.isArray(v)) return [];
  return v.filter((p): p is Record<string, unknown> => Boolean(p) && typeof p === 'object' && typeof (p as { id?: unknown }).id === 'string')
    .map((p) => ({
      id: p.id as string,
      name: typeof p.name === 'string' && p.name.trim() ? p.name : 'Prospect',
      flag: typeof p.flag === 'string' && p.flag.trim() ? p.flag : null,
      tags: Array.isArray(p.tags) ? p.tags.filter((t): t is string => typeof t === 'string' && Boolean(t.trim())) : [],
      note: typeof p.note === 'string' && p.note.trim() ? p.note : null,
    }));
}
export const CALLER_API = '/api/voice/caller';
export const CALLER_FAILED = 'Couldn’t look up this caller — try again.';
export async function callerProfile(callId: string): Promise<CallerProfile> {
  const { data } = await supabase.auth.getSession();
  const path = `${CALLER_API}?call=${encodeURIComponent(callId)}`;
  const r = await fetch(path, { headers: { Authorization: `Bearer ${data.session?.access_token ?? ''}` }, cache: 'no-store' }).catch(() => null);
  const j = r?.ok ? ((await r.json().catch(() => null)) as { number?: string | null; people?: unknown; prospects?: unknown } | null) : null;
  if (!j || !Array.isArray(j.people)) {
    noteFailure({ reason: r ? 'server' : 'network', message: CALLER_FAILED, apiPath: CALLER_API, apiStatus: r?.status });
    throw new Error(CALLER_FAILED);
  }
  return { number: j.number ?? null, people: j.people as CallerPerson[], prospects: callerProspects(j.prospects) };
}

/** A call on the panel, from our own record of it: which way, the other number, the event. null = not readable. */
export interface CallInfo { direction: CallRow['direction'] | null; number: string | null; event_id: string | null; event: string | null }
export async function callInfo(id: string): Promise<CallInfo | null> {
  const r = await supabase.from('event_calls').select('from_number, to_number, direction, event_id, events(event_name)').eq('id', id).maybeSingle();
  if (r.error || !r.data) return null;
  const d = r.data as { from_number: string | null; to_number: string | null; direction: CallRow['direction'] | null; event_id: string | null;
    events: { event_name: string | null } | { event_name: string | null }[] | null };
  const ev = Array.isArray(d.events) ? d.events[0] : d.events;
  return { direction: d.direction ?? null, number: d.direction && d.direction !== 'in' ? d.to_number : d.from_number, event_id: d.event_id ?? null, event: ev?.event_name ?? null };
}

export const HISTORY_FAILED = 'Couldn’t load this number’s calls.';
/** Every call the event has had with one number, newest first (at most 100), with each one's notes and recording summary. */
export async function numberCalls(eventId: string, number: string): Promise<CallRow[]> {
  if (!/^\+\d{10,15}$/.test(number)) return [];
  const r = await supabase.from('event_calls').select(CALL_COLS).eq('event_id', eventId)
    .or(`from_number.eq.${number},to_number.eq.${number}`).order('started_at', { ascending: false }).limit(100);
  if (r.error) throw r.error;
  // a call out is from the line's own number, so only the other end counts
  const calls = ((r.data ?? []) as unknown as CallRow[]).filter((c) => otherParty(c) === number);
  if (!calls.length) return calls;
  const ids = calls.map((c) => c.id);
  const [v, n] = await Promise.all([
    supabase.from('event_voicemails').select(VM_COLS).in('call_id', ids),
    supabase.from('event_call_notes').select(NOTE_COLS).in('call_id', ids),
  ]);
  const vms = new Map(((v.data ?? []) as VoicemailRow[]).map((m) => [m.call_id, m]));
  const notes = new Map(((n.data ?? []) as CallNoteRow[]).map((m) => [m.call_id, m]));
  for (const c of calls) { c.voicemail = vms.get(c.id) ?? null; c.note = notes.get(c.id) ?? null; }
  // who took each call; a name that can't be read leaves the row without one
  const staff = [...new Set(calls.map((c) => (c.direction === 'in' ? c.answered_by : c.placed_by)).filter((v): v is string => Boolean(v)))];
  if (staff.length) {
    const p = await supabase.from('user_profiles').select('id, display_name').in('id', staff);
    const names = new Map(((p.data ?? []) as { id: string; display_name: string | null }[]).map((u) => [u.id, u.display_name?.trim() || null]));
    for (const c of calls) { const u = c.direction === 'in' ? c.answered_by : c.placed_by; c.who = u ? names.get(u) ?? 'Someone on the team' : null; }
  }
  return calls;
}

/** How a call ended, in words (Recent calls keeps its own copy with "Left a message"). */
export const OUTCOME_LABEL: Record<string, string> = {
  ringing: 'Ringing', answered: 'Answered', missed: 'No answer', abandoned: 'Hung up before an answer', blocked: 'Not placed — limit reached', failed: 'Failed',
};

// ── the script assistant and caller questions (call-center B3) ─────────────────────────────────────────────────────────────

export const ASSIST_API = '/api/calls/assist';
export const QUESTIONS_API = '/api/calls/questions';
export const ASK_FAILED = 'Couldn’t answer just now — try again, or send it to the event’s managers.';
export const SEND_FAILED = 'Couldn’t send the question — try again.';
export const QUESTIONS_LOAD_FAILED = 'Couldn’t load the caller questions — try again.';

/** What the caller asked, answered from the event's details and its managers' answers: a short script to read out. */
export function askAssistant(eventId: string, question: string) {
  return postApi<AssistAnswer>(ASSIST_API, { event_id: eventId, question }, { timeoutMs: 15_000 });
}

/** Saves the question for the event's managers, with the answer the panel showed (none when it couldn't answer). */
export function sendQuestion(body: { event_id: string; call_id: string | null; question: string; ai_answer?: string }) {
  return postApi<SentQuestion>(QUESTIONS_API, body);
}

// named columns only: who asked and who answered stay with the data tier
const QUESTION_COLS = 'id, event_id, call_id, question, question_norm, ai_answer, status, admin_answer, answered_at, created_at';

/** Every question the event's team has sent, newest first, as far as this person may read them. */
export async function listQuestions(eventId: string): Promise<CallQuestionRow[]> {
  const r = await fetchAll<CallQuestionRow>((a, b) => supabase.from('call_questions').select(QUESTION_COLS, { count: 'exact' })
    .eq('event_id', eventId).order('created_at', { ascending: false }).order('id').range(a, b));
  if (r.error) throw r.error;
  return r.data;
}

/** One wording on the Caller questions page: its questions newest first, how often it was asked, and where it stands. */
export interface QuestionGroup {
  norm: string; rows: CallQuestionRow[]; count: number; last: string;
  /** The newest wording, as the team typed it. */
  sample: string;
  ai_answer: string | null; admin_answer: string | null;
  /** Open while any of its questions waits; answered once one is, and none waits; else dismissed. */
  status: QuestionStatus;
}

/** The questions grouped by wording, the most asked first, then the most recently asked. */
export function groupQuestions(rows: CallQuestionRow[]): QuestionGroup[] {
  const groups = new Map<string, CallQuestionRow[]>();
  for (const q of [...rows].sort((a, b) => b.created_at.localeCompare(a.created_at))) {
    const g = groups.get(q.question_norm);
    if (g) g.push(q);
    else groups.set(q.question_norm, [q]);
  }
  const out: QuestionGroup[] = [];
  for (const [norm, qs] of groups) {
    out.push({
      norm, rows: qs, count: qs.length, last: qs[0]!.created_at, sample: qs[0]!.question,
      ai_answer: qs.find((q) => q.ai_answer)?.ai_answer ?? null, admin_answer: qs.find((q) => q.admin_answer)?.admin_answer ?? null,
      status: qs.some((q) => q.status === 'sent') ? 'sent' : qs.some((q) => q.status === 'answered') ? 'answered' : 'dismissed',
    });
  }
  return out.sort((a, b) => b.count - a.count || b.last.localeCompare(a.last));
}

/** The manager's answer for every question of one wording on the event; the assistant reads it from then on. */
export async function answerQuestions(eventId: string, norm: string, answer: string, expected?: number) {
  const { data } = await supabase.auth.getSession();
  return writeUpdate(supabase, 'call_questions',
    { status: 'answered', admin_answer: answer, answered_by: data.session?.user.id ?? null, answered_at: new Date().toISOString() },
    { event_id: eventId, question_norm: norm }, { expected });
}

/** Sets aside every question of one wording on the event, unanswered. */
export function dismissQuestions(eventId: string, norm: string, expected?: number) {
  return writeUpdate(supabase, 'call_questions', { status: 'dismissed' }, { event_id: eventId, question_norm: norm }, { expected });
}
